'use client';

// ── Scalping Rule · "Example · 10 trades" ─────────────────────────────────────
// Figma "Moldura · Exemplo · 10 trades": desktop 12071:59962 (792×678), mobile
// 12471:9227 (366×904). Every size is Figma px / 8 in rem. The hold-time bars
// and the 15-second line are not proportional between the two Figma frames
// (desktop track 504px, line at 242; mobile track 94px, line at 50), so each
// breakpoint carries its own numbers via CSS variables. Values are examples.

import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { CheckmarkIcon } from '../Icons';
import { ExampleLabel, FigureFrame } from '../primitives';

const rem = (px: number) => `${px / 8}rem`;

// width of the filled bar in Figma px: desktop (of 504) / mobile (of 94)
const TRADES = [
  { n: 1, long: true, time: '42s', pnl: '+$120', loss: false, d: 328.277, m: 65.53 },
  { n: 2, long: false, time: '8s', pnl: '+$60', loss: false, d: 192.077, m: 39.256 },
  { n: 3, long: true, time: '95s', pnl: '+$210', loss: false, d: 398.996, m: 80.401 },
  { n: 4, long: false, time: '12s', pnl: '−$40', loss: true, d: 224.381, m: 45.798 },
  { n: 5, long: true, time: '31s', pnl: '+$90', loss: false, d: 302.958, m: 60.583 },
  { n: 6, long: false, time: '5s', pnl: '+$80', loss: false, d: 156.281, m: 31.077 },
  { n: 7, long: true, time: '180s+', pnl: '+$150', loss: false, d: 454, m: 92 },
  { n: 8, long: true, time: '22s', pnl: '−$30', loss: true, d: 274.146, m: 55.642 },
  { n: 9, long: true, time: '64s', pnl: '+$110', loss: false, d: 364.946, m: 73.786 },
  { n: 10, long: false, time: '10s', pnl: '+$70', loss: false, d: 209.538, m: 42.527 },
];

const CHECKS = [
  { label: 'Trades held > 15s', value: '60%', pct: 60, caption: '6 of 10 trades' },
  { label: 'Profit from trades > 15s', value: '79%', pct: 79, caption: '$650 of $820 total profit' },
];

const TEXT = 'mb-0 whitespace-nowrap text-[2rem] leading-[3rem]';
const MUTED = `${TEXT} font-normal text-black/[0.64]`;

function Legend({ className, children, label }: { className?: string; children: ReactNode; label: string }) {
  return (
    <div className={cn('flex items-center gap-[1rem]', className)}>
      {children}
      <p className={MUTED}>{label}</p>
    </div>
  );
}

export default function ScalpingTenTrades() {
  return (
    <FigureFrame data-ui="figure.scalpingTenTrades" padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem] font-geist">
      <ExampleLabel>EXAMPLE · 10 TRADES</ExampleLabel>

      {/* Trades */}
      <div data-ui="figure.scalpingTenTrades.trades" className="flex w-full flex-col gap-[0.75rem]">
        <div className="flex h-[3rem] items-start pl-[13.25rem] lg:h-[4rem] lg:pl-0">
          <p className={`${TEXT} font-medium text-black`}>15s</p>
        </div>
        {TRADES.map((t) => (
          <div key={t.n} data-ui="figure.scalpingTenTrades.trade" className="flex w-full items-center gap-[2rem]">
            <p className={`${MUTED} w-[9rem] shrink-0`}>Trade {t.n}</p>
            <div
              className="relative h-[2rem] min-w-0 flex-1 rounded-[0.25rem] bg-black/[0.04]"
              style={{ '--w-m': rem(t.m), '--w-d': rem(t.d) } as CSSProperties}
            >
              <div
                className={cn(
                  'absolute left-0 top-0 h-[2rem] w-[var(--w-m)] rounded-[0.25rem] lg:w-[var(--w-d)]',
                  t.long ? 'bg-[#228e71]' : 'bg-black/[0.16]',
                )}
              />
              <div className="absolute left-[6.25rem] top-[-0.5rem] h-[3rem] w-px bg-black lg:left-[30.25rem]" />
            </div>
            <p className={`${MUTED} w-[7rem] shrink-0 text-right`}>{t.time}</p>
            <p className={cn(`${TEXT} w-[8rem] shrink-0 text-right font-medium`, t.loss ? 'text-[#ac0003]' : 'text-[#228e71]')}>
              {t.pnl}
            </p>
          </div>
        ))}
      </div>

      {/* Legenda */}
      <div
        data-ui="figure.scalpingTenTrades.legend"
        className="flex w-full flex-col items-center gap-[1rem] lg:flex-row lg:items-start lg:gap-[3rem]"
      >
        <Legend className="w-full lg:w-auto" label="Held longer than 15 seconds">
          <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] bg-[#228e71]" />
        </Legend>
        <Legend className="w-full lg:w-auto" label="Held 15 seconds or less">
          <span className="h-[1.25rem] w-[1.25rem] shrink-0 rounded-[0.25rem] bg-black/[0.16]" />
        </Legend>
        <Legend className="w-[15.375rem] lg:w-auto" label="15-second line">
          <span className="flex w-[1rem] shrink-0 justify-center lg:w-[0.25rem]">
            <span className="h-[1.5rem] w-[0.25rem] bg-black" />
          </span>
        </Legend>
      </div>

      {/* Resultados */}
      <div data-ui="figure.scalpingTenTrades.results" className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-stretch">
        {CHECKS.map((c) => (
          <div
            key={c.label}
            data-ui="figure.scalpingTenTrades.check"
            className="flex h-[18.25rem] min-w-0 flex-1 flex-col items-start gap-[1rem] rounded-[0.25rem] bg-[#f2f4f3] p-[2rem]"
          >
            <div className="flex w-full items-center justify-between">
              <p className={MUTED}>{c.label}</p>
              <div className="flex items-center gap-[0.5rem] text-[#228e71]">
                <CheckmarkIcon className="h-[1.75rem] w-[1.75rem]" />
                <p className={`${TEXT} font-medium`}>min. 50%</p>
              </div>
            </div>
            <p className="mb-0 whitespace-nowrap text-[3.5rem] font-medium leading-[4.5rem] text-black">{c.value}</p>
            <div className="relative h-[0.75rem] w-full rounded-[0.25rem] bg-black/[0.08]">
              <div className="absolute left-0 top-0 h-[0.75rem] rounded-[0.25rem] bg-[#228e71]" style={{ width: `${c.pct}%` }} />
              <div className="absolute left-[calc(50%-0.125rem)] top-[-0.5rem] h-[1.75rem] w-[0.25rem] bg-black" />
            </div>
            <p className={MUTED}>{c.caption}</p>
          </div>
        ))}
      </div>

      <p className="mb-0 text-[2rem] leading-[3rem] text-black/[0.64]">
        Both checks are above 50% — the Scalping Rule is met and a payout can be requested.
      </p>
    </FigureFrame>
  );
}
